import { MouseEventHandler } from 'react';
import styled from '@emotion/styled';
import { Chip, ChipProps, Fade, Tooltip, Typography } from '@mui/material';

import focusConfig from '@/components/Focus/configs';
import type { TitleEntity } from '@/redux/slices/slice_types';
import type { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { generateHexFromString } from '@/_utils/colorUtils';
import { useAppSelector } from '@/redux/hooks';
import { selectSelectedHierarchy } from '@/redux/selectors/orderPlacementSelectors';

type ScheduleVersionChipProps = ChipProps & {
  version: TitleEntity | TitleEntityV2;
  selectedVersions: (TitleEntity | TitleEntityV2)['id'][];
  size?: ChipProps['size'];
  onVersionClick?: (selectedVersion: TitleEntity | TitleEntityV2) => void;
};

const ScheduleVersionChip = ({ version, selectedVersions, size = 'medium', onVersionClick, ...props }: ScheduleVersionChipProps) => {
  const selectedHierarchy = useAppSelector(selectSelectedHierarchy);
  const versionColors: Record<string, string> = useConfigValue(['versionColors']) ?? {};
  const versionChipTooltip: any | undefined = useConfigValue(['orderPlacementConfig', 'versionChipTooltip']);
  const versionType = (version?.version_type || version.meta?.friendly_name || version.name)?.toLowerCase() || '';

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
    onVersionClick?.(version);
  };
  // Derived
  const focusVersionColor = focusConfig.versionColors ? focusConfig.versionColors : undefined;
  const color =
    focusVersionColor && focusVersionColor[versionType]
      ? focusVersionColor[versionType]
      : versionColors?.[versionType] || generateHexFromString(versionType) || '#000';
  const isSelected = Boolean(selectedVersions.find((v) => v === version.id));
  const tip = versionChipTooltip ? versionChipTooltip(version, undefined, selectedHierarchy) : version.external_id || version.id;

  return (
    <Fade in>
      <div>
        <Tooltip arrow title={tip}>
          <StyledChip
            clickable
            key={version.id}
            label={
              <ChipLabel noWrap size={size}>
                {versionType.toUpperCase()}
              </ChipLabel>
            }
            size={size}
            variant="outlined"
            color="primary"
            disabled={version.disabled}
            selected={isSelected}
            bg={color}
            onClick={handleClick}
            {...props}
          />
        </Tooltip>
      </div>
    </Fade>
  );
};

export default ScheduleVersionChip;

const StyledChip = styled(Chip)<{ selected: boolean; bg: string }>(({ theme, size, selected, bg }) => ({
  margin: theme.spacing(0.25, 0, 0.25, 1),
  border: '1px solid',
  color: selected ? theme.palette.background.default : bg,
  borderColor: bg,
  minWidth: 80,
  borderRadius: 12,
  paddingLeft: theme.spacing(size === 'small' ? 0.5 : 0),
  paddingRight: theme.spacing(size === 'small' ? 0.5 : 0),

  '&.MuiChip-root.MuiChip-outlined': {
    backgroundColor: theme.customOverrides?.TitleVersionChip
      ? selected
        ? bg
        : theme.customOverrides?.TitleVersionChip?.backgroundColor
      : selected
        ? bg
        : 'transparent',
    color: theme.customOverrides?.TitleVersionChip ? (selected ? theme.customOverrides?.TitleVersionChip?.color : bg) : undefined
  },

  '&.Mui-disabled': {
    opacity: `${theme.palette.mode == 'light' ? 0.2 : 0.4} !important}`
  },

  '&.MuiChip-root.MuiChip-outlined:hover': {
    backgroundColor: theme.customOverrides?.TitleVersionChip
      ? selected
        ? theme.customOverrides?.TitleVersionChip?.backgroundColor
        : bg
      : selected
        ? 'transparent'
        : bg,
    color: theme.customOverrides?.TitleVersionChip
      ? selected
        ? theme.customOverrides?.TitleVersionChip?.color
        : bg
      : selected
        ? bg
        : theme.palette.background.default
  }
}));

const ChipLabel = styled(Typography, { shouldForwardProp: (prop) => prop !== 'size' })<{ size: string }>(({ size }) => ({
  fontSize: size === 'small' ? '.9rem' : '1rem',
  maxWidth: 150
}));
